import {AGE_CONSENT_TYPE, TOS_CONSENT_TYPE, PRIVACY_CONSENT_TYPE} from '../common';

const commonHeaders = {'X-Requested-With': 'XMLHttpRequest'};

class ConsentService {
    getUserConsents = (applicationName, languageCode) => {
        var isUserConsentChecked = window.sessionStorage.getItem('UserConsentChecked');
        if (isUserConsentChecked) {
            return Promise.resolve(null);
        }

        var getConsentsUrl = '/api/consents';
        if (applicationName) {
            getConsentsUrl += '?applicationName=' + applicationName;
        }
        if (languageCode) {
            getConsentsUrl += applicationName ? '&' : '?';
            var langParts = languageCode.split('-');
            if (langParts.length === 2) {
                var langPart = langParts[0].toLowerCase();
                var countryPart = langParts[1].toLowerCase();
                languageCode = countryPart + '-' + langPart;
            }
            getConsentsUrl += 'languageCode=' + languageCode;
        }

        var serverDate;
        return fetch(getConsentsUrl, {
            credentials: 'same-origin', 
            headers: {...commonHeaders}
        })
            .then((response) => {
                if (response.status !== 200) {
                    throw new Error('GET /api/consents returned not OK');
                }
                serverDate = response.headers.get('Date');
                return response.json();
            }).catch((e) => {
                console.log('Failed to fetch consents', e);
                throw new Error(e.message);
            }).then((userConsents) => {
                var contentPromises = [];
                var consentIndexes = [];

                var isConsentAcceptanceRequired = false;
                var index = userConsents.length;
                while (index--) {
                    if (userConsents[index].required && !userConsents[index].accepted) {
                        isConsentAcceptanceRequired = true;
                    }
                    if (userConsents[index].accepted) { // No need to display accepted consents
                        userConsents.splice(index, 1);
                    }
                }

                if (!isConsentAcceptanceRequired) { // Returns null if not need to display modal at all
                    window.sessionStorage.setItem('UserConsentChecked', true);
                    return null;
                }

                const CONSENTS_TYPE_PRIORITY_LIST = ['PRIVACY', 'TOS', 'PID', 'DATA_TRANSFER', 'PID_HEALTH', 'AGE', 'MARKETING'];
                var userConsentsSortedByType = [];
                // Hackety hack to sort consents for ui
                for (let i = 0; i < CONSENTS_TYPE_PRIORITY_LIST.length; i++) {
                    var nextUserConsent = userConsents.filter(userConsent => userConsent.type === CONSENTS_TYPE_PRIORITY_LIST[i]);
                    if (nextUserConsent.length > 0) {
                        userConsentsSortedByType.push(nextUserConsent[0]);
                        userConsents = userConsents.filter(isDifferentTypeWith(nextUserConsent));
                    }
                }
                // If we fetch consents that aren't in the priority list, let's just add them to the end of the list
                for (let i = 0; i < userConsents.length; i++) {
                    userConsentsSortedByType.push(userConsents[i]);
                }
                userConsents = userConsentsSortedByType;

                var ageConsent = userConsents.filter(consent => consent.type === AGE_CONSENT_TYPE);
                if (ageConsent.length > 0) {
                    ageConsent[0].serverDate = serverDate;
                }

                // Localization keys in database work with Legacy, not with i18next, need to assing the namespace with :
                for (let i = 0; i < userConsents.length; i++) {
                    if (userConsents[i].name.indexOf(':') === -1) {
                        userConsents[i].name = userConsents[i].name.replace('.', ':');
                    }
                    if (userConsents[i].description.indexOf(':') === -1) {
                        userConsents[i].description = userConsents[i].description.replace('.', ':');
                    }
                }

                for (let i = 0; i < userConsents.length; i++) { // Fetches legal texts if any exists
                    if (userConsents[i].contentUrl) {
                        // Consent's content that appears in textarea. Let's add url parameter to get json response
                        var consentType = userConsents[i].type;
                        var contentPromise = fetch(userConsents[i].contentUrl + '?content=1')
                            .then((response) => {
                                return response.json();
                            })
                            .catch(handleFailedLegalFetch(consentType))
                            .then((consentContent) => {
                                return consentContent.body;
                            });
                        contentPromises.push(contentPromise);
                        consentIndexes.push(i);
                    }
                }
                return {userConsents, consentIndexes, contentPromises};
            }).catch((e) => {
                console.log('Failed to parse consent promise', e);
                throw new Error(e.message);
            }).then((data) => {
                // After all (if any) content fetches are resolved, assing content to returned object
                if (data) {
                    return Promise.all(data.contentPromises).then((contentPromises) => {
                        for (var i = 0; i < data.consentIndexes.length; i++) {
                            data.userConsents[data.consentIndexes[i]].content = contentPromises[i];
                        }
                        return data.userConsents;
                    }).catch((e) => {
                        console.log('Failed to parse legal promise', e);
                        throw new Error(e.message);
                    });
                }
                return null; // return consents without legal texts
            }).catch((e) => {
                console.log('Failed to resolve legal text promise', e);
                throw new Error(e.message);
            });
    }

    postUserConsents = (userConsents) => {
        window.sessionStorage.setItem('UserConsentChecked', true);

        // Removes ui helper properties from consents if any exits
        userConsents.forEach(function(userConsent) {
            delete userConsent.isContentVisible;
            delete userConsent.isMissingCheck;
            delete userConsent.content;
            delete userConsent.isMissing;
            delete userConsent.isMissingAgeSelect;
            delete userConsent.isMissingGuardianCheck;
            delete userConsent.isMissingGuardianCheckIcon;
            delete userConsent.serverDate;
        });
        return fetch('/api/consents', {method: 'POST', body: JSON.stringify(userConsents),
            headers: { 'Content-Type': 'application/json', 
            ...commonHeaders}, 
            credentials: 'same-origin'})
            .then((response) => {
                if (!response.ok) {
                    throw new Error('POST /api/consents returned not OK');
                }
            });
    }
}

export default new ConsentService();

function isDifferentTypeWith(nextUserConsent) {
    return userConsent => userConsent.type !== nextUserConsent[0].type;
}

function handleFailedLegalFetch(consentType) {
    return (error) => {
        console.error('Error on fetching legal text, fallbacking to English texts:', error);
        var consentDefaultUrl;
        if (consentType === PRIVACY_CONSENT_TYPE) {
            consentDefaultUrl = 'https://www.polar.com/en/legal/privacy-notice?content=1';
        }
        else if (consentType === TOS_CONSENT_TYPE) {
            consentDefaultUrl = 'https://www.polar.com/en/legal/terms-of-use?content=1';
        }
        return fetch(consentDefaultUrl)
            .then((response) => {
                return response.json();
            })
            .catch((e) => {
                console.error('Error on fetching fallback legal text:', e);
                return '';
            })
            .then((consentContent) => {
                return consentContent;
            });
    };
}




// WEBPACK FOOTER //
// ./src/ConsentsLoginModal/ConsentService.js